import React, { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactCountryFlag from "react-country-flag";
import { AiModal } from "../Quote/Leads";
import { getFileUrl } from "../../../core/config/api.js";
import { conditionFormatter } from "../../../core/utils/formatters";
import useWhatsappMatch from "./useWhatsappMatch";
import "./lead-card.scss";

dayjs.extend(relativeTime);

const regionToCountryCode = {
  uk: "GB", us: "US", europe: "EU", "middle-east": "AE", asia: "JP",
  australia: "AU", "north-america": "US", "south-america": "BR", africa: "ZA",
  germany: "DE", france: "FR", italy: "IT", spain: "ES", switzerland: "CH",
  netherlands: "NL", belgium: "BE", canada: "CA", japan: "JP", china: "CN",
  india: "IN", singapore: "SG", "hong-kong": "HK", uae: "AE", qatar: "QA", saudi: "SA",
};

const getCountryCode = (region) => {
  if (!region) return null;
  const normalized = region.toLowerCase().trim();
  if (regionToCountryCode[normalized]) return regionToCountryCode[normalized];
  if (normalized.length === 2) return normalized.toUpperCase();
  return null;
};

const MESSAGE_LIMIT = 160;

/**
 * A lead is somebody asking for a watch, so the card leads with the person and
 * their words. The stock you hold sits underneath, labelled as the match.
 *
 * The photo prefers the image THEY sent on WhatsApp (`whatsapp_image_url` on
 * the match payload) and falls back to your own catalogue shot. Only their photo
 * is marked — a green edge and the WhatsApp glyph — because that is the case
 * worth spotting; a catalogue shot is the unremarkable default.
 */
const LeadsGridCard = ({ item, type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isOpportunityTab = type === "in-stock" || type === "in-consignment";

  // Aborts on unmount, caches by product|message, and de-duplicates concurrent
  // requests for the same pair. See useWhatsappMatch for why.
  const { matchData, isLoading: isLoadingMatch } = useWhatsappMatch(
    item.product_id,
    item.message_id,
  );

  const productImage = item?.product?.images?.[0]?.id ? getFileUrl(item.product.images[0].id) : null;
  const brandName = item?.product?.brand?.name || "";
  const modelName = item?.product?.model?.name || "";
  const reference = item?.product?.reference_number || "";
  const year = item?.year || "";
  const condition = conditionFormatter(item?.condition) || "N/A";

  const messageText = matchData?.message_text || "";
  const senderName = matchData?.sender_name || "";
  const groupName = matchData?.group_name || "";
  const isLongMessage = messageText.length > MESSAGE_LIMIT;
  const displayMessage = isLongMessage && !expanded ? messageText.substring(0, MESSAGE_LIMIT) : messageText;
  const region = matchData?.region || item?.region || "";
  const countryCode = getCountryCode(region);

  // Their photo first, your stock second. Only the first case is worth marking:
  // a photo from the lead is the watch actually on offer.
  const theirImage = matchData?.whatsapp_image_url || null;
  const photo = theirImage || productImage;

  // When the lead arrived, not when the listing was created — this card is
  // about the enquiry, and "3 months ago" is the number that decides whether
  // it is still worth a reply.
  const askedAt = matchData?.message_date || item?.created_at;
  const askedLabel = askedAt ? dayjs(askedAt).fromNow() : "";
  const sourceLabel = groupName || (matchData ? "WhatsApp" : "Reference match");

  const handleReadMore = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded((prev) => !prev);
  };

  return (
    <>
      <div
        className="lead-card"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen(true); } }}
      >
        <div className="lead-card__who">
          <span className="lead-card__avatar">
            {countryCode ? (
              <ReactCountryFlag countryCode={countryCode} svg />
            ) : (
              (senderName || "?").trim().charAt(0).toUpperCase()
            )}
          </span>

          <span className="lead-card__whotext">
            <span className="lead-card__name">{senderName || "Anonymous lead"}</span>
            <span className="lead-card__meta">
              {[askedLabel, sourceLabel].filter(Boolean).join(" · ")}
            </span>
          </span>

          {matchData && !theirImage && (
            <span className="lead-card__wa">
              <img src="/assets/icons/whatsapp-green-circle.svg" alt="From WhatsApp" />
            </span>
          )}
        </div>

        {isLoadingMatch ? (
          <p className="lead-card__loading">Loading message…</p>
        ) : messageText ? (
          <p className="lead-card__quote">
            “{displayMessage}
            {isLongMessage && !expanded ? "…" : ""}”
            {isLongMessage && (
              <span className="lead-card__more" onClick={handleReadMore}>
                {expanded ? " Show less" : " Read more"}
              </span>
            )}
          </p>
        ) : (
          <p className="lead-card__quote is-empty">
            No message. Matched on reference{reference ? ` ${reference}` : ""}.
          </p>
        )}

        <div className="lead-card__match">
          <span className={`lead-card__photo${theirImage ? " is-theirs" : ""}`}>
            {photo ? (
              <>
                <img className="lead-card__photo-main" src={photo} alt={`${brandName} ${modelName}`.trim() || "Watch"} />
                {theirImage && (
                  <span className="lead-card__wamark">
                    <img src="/assets/icons/whatsapp-green-circle.svg" alt="Photo sent on WhatsApp" />
                  </span>
                )}
              </>
            ) : (
              <span className="lead-card__photo-empty">No image</span>
            )}
          </span>

          <span className="lead-card__matchtext">
            <span className="lead-card__label">You have</span>
            <span className="lead-card__model">{modelName || brandName || "Listing"}</span>
            <span className="lead-card__spec">
              {[year, condition, reference].filter(Boolean).join(" · ")}
            </span>
          </span>
        </div>
      </div>

      <AiModal isOpen={isOpen} item={item} hide={() => setIsOpen(false)} isOpportunityModal={isOpportunityTab} approveBlack />
    </>
  );
};

export default LeadsGridCard;
